import {
  BlockingReason,
  ScoringContext,
  FinalDecision,
  TrackAVerdict,
  TrackBVerdict,
  VNeuroneMode,
  type CompositeInfo,
  type CompositeVerdict,
  type ProviderAvailability,
  type ProviderState,
  type RegulaState,
  type TrackA,
  type TrackB,
  type VNeuroneState,
} from "@/core/compliance/entities/CompositeVerdict";
import type { CompositeVerdictAudit } from "@/core/compliance/entities/CompositeVerdictAudit";

// ---------------------------------------------------------------------------
// DTO shapes (private to the details/ layer)
// ---------------------------------------------------------------------------

export interface ProviderStateDto {
  availability: string;
  status: string | null;
  pepHit: boolean;
  sanctionsHit: boolean;
  matchedLists: string[];
  lastScreenedAt: string | null;
}

export interface RegulaStateDto {
  availability: string;
  screeningStatus: string | null;
  documentNumber: string | null;
  lastScreenedAt: string | null;
}

export interface VNeuroneStateDto {
  availability: string;
  outboundStatus: string | null;
  externalStatus: string | null;
  reisReference: string | null;
  pendingOver48h: boolean;
  lastCalledAt: string | null;
  refusalRule?: string | null;
  refusalReason?: string | null;
}

export interface TrackADto {
  regula: RegulaStateDto;
  lseg: ProviderStateDto;
  complyAdvantage: ProviderStateDto;
  verdict: string;
}

export interface TrackBDto {
  vneurone: VNeuroneStateDto;
  verdict: string;
}

export interface CompositeInfoDto {
  weightTrackA: number;
  weightTrackB: number;
  vneuroneMode: string;
  finalDecision: string;
  canCreateCustomer: boolean;
  requiresScreeningHold: boolean;
  blockingReason: string | null;
  scoringContext?: ScoringContextDto | null;
}

export interface ScoringContextDto {
  compositorMode?: string | null;
  compositorReason?: string | null;
  weightTrackA?: number | null;
  weightTrackB?: number | null;
  computedFrom?: string | null;
}

export interface CompositeVerdictDto {
  businessKey: string;
  computedAt: string;
  trackA: TrackADto;
  trackB: TrackBDto;
  composite: CompositeInfoDto;
}

// ---------------------------------------------------------------------------
// Mapping functions (explicit switch — no bracket notation)
// ---------------------------------------------------------------------------

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapTrackAVerdict(raw: string): TrackAVerdict {
  switch (raw) {
    case "CLEAN": return TrackAVerdict.CLEAN;
    case "PENDING": return TrackAVerdict.PENDING;
    case "BLOCKED": return TrackAVerdict.BLOCKED;
    case "MISSING": return TrackAVerdict.MISSING;
    default: return TrackAVerdict.MISSING;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapTrackBVerdict(raw: string): TrackBVerdict {
  switch (raw) {
    case "CLEAN": return TrackBVerdict.CLEAN;
    case "PENDING": return TrackBVerdict.PENDING;
    case "BLOCKED": return TrackBVerdict.BLOCKED;
    case "REJECT": return TrackBVerdict.REJECT;
    case "MISSING": return TrackBVerdict.MISSING;
    case "DISABLED": return TrackBVerdict.DISABLED;
    default: return TrackBVerdict.DISABLED;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapFinalDecision(raw: string): FinalDecision {
  switch (raw) {
    case "CLEAN": return FinalDecision.CLEAN;
    case "WAIT": return FinalDecision.WAIT;
    case "BLOCKED": return FinalDecision.BLOCKED;
    case "HOLD": return FinalDecision.HOLD;
    case "MANUAL_REVIEW": return FinalDecision.MANUAL_REVIEW;
    // Une valeur inconnue affichée comme BLOCKED ferait passer un dossier qui avance pour un refus
    // définitif — c'est exactement ce qui arrivait à HOLD.
    default: return FinalDecision.UNKNOWN;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapVNeuroneMode(raw: string): VNeuroneMode {
  switch (raw) {
    case "DISABLED": return VNeuroneMode.DISABLED;
    case "SHADOW": return VNeuroneMode.SHADOW;
    case "NON_BLOCKING": return VNeuroneMode.NON_BLOCKING;
    default: return VNeuroneMode.DISABLED;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapBlockingReason(raw: string | null): BlockingReason | null {
  if (raw === null) return null;
  switch (raw) {
    case "TRACK_A_SANCTIONS_HIT": return BlockingReason.TRACK_A_SANCTIONS_HIT;
    case "TRACK_A_SCREENING_MISSING": return BlockingReason.TRACK_A_SCREENING_MISSING;
    case "TRACK_A_PENDING": return BlockingReason.TRACK_A_PENDING;
    case "TRACK_A_ADVERSE_MEDIA_MATCH": return BlockingReason.TRACK_A_ADVERSE_MEDIA_MATCH;
    case "TRACK_B_REJECT": return BlockingReason.TRACK_B_REJECT;
    case "TRACK_B_BLOCKED": return BlockingReason.TRACK_B_BLOCKED;
    case "VNEURONE_PENDING": return BlockingReason.VNEURONE_PENDING;
    case "VNEURONE_PENDING_OVER_48H": return BlockingReason.VNEURONE_PENDING_OVER_48H;
    // Manquants jusqu'ici : le back renvoie ces motifs, le mapping les ramenait à null et l'écran
    // n'affichait alors aucune raison — un dossier bloqué sans motif visible.
    case "PEP_REVIEW_REQUIRED": return BlockingReason.PEP_REVIEW_REQUIRED;
    case "HIGH_RISK_REVIEW_REQUIRED": return BlockingReason.HIGH_RISK_REVIEW_REQUIRED;
    case "TRACK_A_MATCH_TO_QUALIFY": return BlockingReason.TRACK_A_MATCH_TO_QUALIFY;
    default: return null;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
/**
 * Le service répond `PRESENT` ou `MISSING`. Ce mapping attendait `AVAILABLE`, valeur qu'aucune
 * réponse n'a jamais portée : tout tombait dans le cas par défaut et chaque fournisseur était
 * déclaré indisponible, y compris celui qui venait de rendre un verdict.
 */
function mapAvailability(raw: string): ProviderAvailability {
  return raw === "PRESENT" ? "PRESENT" : "MISSING";
}

function mapRegulaState(dto: RegulaStateDto): RegulaState {
  return {
    availability: mapAvailability(dto.availability),
    screeningStatus: dto.screeningStatus,
    documentNumber: dto.documentNumber,
    lastScreenedAt: dto.lastScreenedAt,
  };
}

function mapProviderState(dto: ProviderStateDto): ProviderState {
  return {
    availability: mapAvailability(dto.availability),
    status: dto.status,
    pepHit: dto.pepHit,
    sanctionsHit: dto.sanctionsHit,
    matchedLists: dto.matchedLists,
    lastScreenedAt: dto.lastScreenedAt,
  };
}

function mapVNeuroneState(dto: VNeuroneStateDto): VNeuroneState {
  return {
    availability: mapAvailability(dto.availability),
    outboundStatus: dto.outboundStatus,
    externalStatus: dto.externalStatus,
    reisReference: dto.reisReference,
    pendingOver48h: dto.pendingOver48h,
    lastCalledAt: dto.lastCalledAt,
    // Absents des réponses d'un back plus ancien : on rend null plutôt qu'undefined, pour que
    // l'affichage n'ait qu'un seul cas d'absence à traiter.
    refusalRule: dto.refusalRule ?? null,
    refusalReason: dto.refusalReason ?? null,
  };
}

function mapTrackA(dto: TrackADto): TrackA {
  return {
    regula: mapRegulaState(dto.regula),
    lseg: mapProviderState(dto.lseg),
    complyAdvantage: mapProviderState(dto.complyAdvantage),
    verdict: mapTrackAVerdict(dto.verdict),
  };
}

function mapTrackB(dto: TrackBDto): TrackB {
  return {
    vneurone: mapVNeuroneState(dto.vneurone),
    verdict: mapTrackBVerdict(dto.verdict),
  };
}

/**
 * La composition telle qu'elle a ete consignee, ou null.
 *
 * Un scoring anterieur a sa persistance n'en porte pas : on ne devine rien, l'ecran dira que
 * l'information n'est pas disponible plutot que d'afficher des ponderations qui n'ont pas ete
 * appliquees a ce dossier.
 */
function mapScoringContext(
  dto: ScoringContextDto | null | undefined,
): ScoringContext | null {
  if (!dto) return null;
  return {
    compositorMode: dto.compositorMode ?? null,
    compositorReason: dto.compositorReason ?? null,
    weightTrackA: dto.weightTrackA ?? null,
    weightTrackB: dto.weightTrackB ?? null,
    computedFrom: dto.computedFrom ?? null,
  };
}

function mapCompositeInfo(dto: CompositeInfoDto): CompositeInfo {
  return {
    scoringContext: mapScoringContext(dto.scoringContext),
    weightTrackA: dto.weightTrackA,
    weightTrackB: dto.weightTrackB,
    vneuroneMode: mapVNeuroneMode(dto.vneuroneMode),
    finalDecision: mapFinalDecision(dto.finalDecision),
    canCreateCustomer: dto.canCreateCustomer,
    requiresScreeningHold: dto.requiresScreeningHold === true,
    blockingReason: mapBlockingReason(dto.blockingReason),
  };
}

export function mapVerdictDtoToEntity(dto: CompositeVerdictDto): CompositeVerdict {
  return {
    businessKey: dto.businessKey,
    computedAt: dto.computedAt,
    trackA: mapTrackA(dto.trackA),
    trackB: mapTrackB(dto.trackB),
    composite: mapCompositeInfo(dto.composite),
  };
}

// ---------------------------------------------------------------------------
// Historique des evaluations de la porte
// ---------------------------------------------------------------------------

export interface CompositeVerdictAuditDto {
  id: number;
  businessKey: string;
  evaluatedAt: string;
  actorId?: string | null;
  triggerSource?: string | null;
  finalDecision: string;
  canCreateCustomer: boolean;
  blockingReason?: string | null;
  trackAVerdict?: string | null;
  trackBVerdict?: string | null;
  vneuroneOutboundStatus?: string | null;
  vneuroneExternalStatus?: string | null;
  vneuroneMode?: string | null;
  weightTrackA?: number | null;
  weightTrackB?: number | null;
  rationale?: string | null;
}

/**
 * Les verdicts et statuts restent ici des chaînes brutes, à dessein.
 *
 * Une ligne d'audit est la trace de ce que le dispositif a rendu ce jour-là ; la replier sur les
 * valeurs que cette version du front connaît en réécrirait le contenu. Un verdict d'hier disparu
 * du code d'aujourd'hui doit rester lisible tel qu'il a été écrit.
 */
export function mapVerdictAuditDtoToEntity(dto: CompositeVerdictAuditDto): CompositeVerdictAudit {
  return {
    id: dto.id,
    businessKey: dto.businessKey,
    evaluatedAt: dto.evaluatedAt,
    actorId: dto.actorId ?? null,
    triggerSource: dto.triggerSource ?? null,
    finalDecision: dto.finalDecision,
    canCreateCustomer: dto.canCreateCustomer,
    blockingReason: dto.blockingReason ?? null,
    trackAVerdict: dto.trackAVerdict ?? null,
    trackBVerdict: dto.trackBVerdict ?? null,
    vneuroneOutboundStatus: dto.vneuroneOutboundStatus ?? null,
    vneuroneExternalStatus: dto.vneuroneExternalStatus ?? null,
    vneuroneMode: dto.vneuroneMode ?? null,
    weightTrackA: dto.weightTrackA ?? null,
    weightTrackB: dto.weightTrackB ?? null,
    rationale: dto.rationale ?? null,
  };
}
